import { z } from "zod";
import type { BrandCDTLevel } from "@CommonTypes/algo/generated-pogger";

/** Which CDT level is the brand, so a run with an aisle break can keep brands whole across it. */
export const RoutePutCategories_categoryId_AisleBreakBrandLevel =
  "/categories/:categoryId/aisle-break-brand-level";

export const ParamsCategoriesAisleBreakBrandLevelSchema = z.object({
  categoryId: z.coerce.number().int().positive(),
});

export type ParamsPutCategories_categoryId_AisleBreakBrandLevel = z.infer<
  typeof ParamsCategoriesAisleBreakBrandLevelSchema
>;

/** The pogger's `BrandCDTLevel`, keyed by it, so a level added or dropped there fails to compile. */
const brandCdtLevels: { [Level in BrandCDTLevel]: Level } = {
  cdt0: "cdt0",
  cdt1: "cdt1",
  cdt2: "cdt2",
};

export const BrandCdtLevelSchema = z.enum(brandCdtLevels);

/** One level the picker offers, named the way the category's CDT names it. */
export const AisleBreakBrandLevelOptionSchema = z.object({
  level: BrandCdtLevelSchema,
  name: z.string(),
});

export type AisleBreakBrandLevelOption = z.infer<typeof AisleBreakBrandLevelOptionSchema>;

/** `release_id` and `tree_id` are echoed from the error's detail: the CDT the refused run read. */
export const PayloadPutCategoriesAisleBreakBrandLevelSchema = z.object({
  level: BrandCdtLevelSchema,
  release_id: z.number().int().positive().nullable(),
  tree_id: z.number().int().positive().nullable(),
});

export type PayloadPutCategories_categoryId_AisleBreakBrandLevel = z.infer<
  typeof PayloadPutCategoriesAisleBreakBrandLevelSchema
>;

/** `brand_attr` is the CDT Studio attribute now saved as the category's brand role. */
export const ResponsePutCategoriesAisleBreakBrandLevelSchema = z.object({
  level: BrandCdtLevelSchema,
  brand_attr: z.string(),
  resolved_from: z.enum(["release_levels", "tree_level_names"]),
});

export type ResponsePutCategories_categoryId_AisleBreakBrandLevel = z.infer<
  typeof ResponsePutCategoriesAisleBreakBrandLevelSchema
>;

export const SPLIT_BRAND_LEVEL_UNRESOLVED = "SPLIT_BRAND_LEVEL_UNRESOLVED";

/**
 * On a store pog the generation refused: its store has an aisle break and the category's CDT does
 * not say which level is the brand. `levels` is empty when the run read no CDT levels at all.
 */
export const SplitBrandLevelUnresolvedDetailSchema = z.object({
  code: z.literal(SPLIT_BRAND_LEVEL_UNRESOLVED),
  category_id: z.number().int().positive(),
  levels: z.array(AisleBreakBrandLevelOptionSchema),
  /** Stores in the same run whose fixtures declare an aisle break, this one included. */
  affected_store_count: z.number().int().nonnegative(),
  release_id: z.number().int().positive().nullable(),
  tree_id: z.number().int().positive().nullable(),
});

export type SplitBrandLevelUnresolvedDetail = z.infer<typeof SplitBrandLevelUnresolvedDetailSchema>;

/** A store pog's typed error, beside its prose `error`; one variant so far. */
export const StorePogErrorDetailSchema = z.discriminatedUnion("code", [
  SplitBrandLevelUnresolvedDetailSchema,
]);

export type StorePogErrorDetail = z.infer<typeof StorePogErrorDetailSchema>;
